import { useState } from 'react';
import { createPortal } from 'react-dom';
import { RiShieldKeyholeLine, RiLoader4Line, RiCloseLine } from '@remixicon/react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { ROLES, normalizeRole } from '@/lib/permissions';
import { useMfaStepUpGate, isMfaCancelled } from '../hooks/MfaStepUpGate';

// PROD-only recovery action. Kept out of DEV / LOCAL so accidental clicks
// during development can't wipe a real TOTP secret.
const IS_PROD = process.env.REACT_APP_ENVIRONMENT === 'PROD';

/**
 * Reset MFA button — clears a user's TOTP factors via
 * `PATCH /users/{userId}/security/mfa { reset: true }`. The user is
 * prompted to enroll again on their next sign-in.
 *
 * Visibility gates (all must hold, else the component renders null):
 *   - Environment is PROD
 *   - Viewer is admin or super_admin
 *   - Viewer is NOT the same user as the target (self-reset would wipe
 *     the viewer's own TOTP mid-session and lock them out until they
 *     re-enroll — Account Settings has the intended self-service path)
 *   - Target user has MFA enrolled (`mfaEnabled === true`) — no point
 *     offering a reset on a user with nothing to reset.
 *   - `userId` is present
 *
 * Step-up MFA is delegated to the page-level MfaStepUpGate: the admin
 * either already verified for this Account Settings visit (one-tap
 * Reset) or gets prompted for their own TOTP through the shared modal.
 * A per-button "Are you sure?" confirmation still gates the PATCH — the
 * shared gate covers identity, this covers intent.
 */
const ResetMfaButton = ({ userId, memberName, mfaEnabled, className = '', variant = 'default' }) => {
  const { userProfile } = useAuthStore();
  const { gate } = useMfaStepUpGate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Sticky "just reset" flag — hides the button for the rest of the
  // current drawer render. Reopening the drawer rehydrates from the
  // React Query cache, so we also patch that cache below to flip
  // mfa_enabled to false — otherwise the button would come back.
  const [didReset, setDidReset] = useState(false);

  if (!IS_PROD) return null;
  if (!userId) return null;
  if (mfaEnabled === false) return null; // undefined still shows (BE hasn't returned the field yet)
  if (didReset) return null;
  // Self-reset guard — refuse to wipe the viewer's own TOTP factor
  // mid-session. AddMemberDrawer already hides Edit for self-rows,
  // but OrganizationDrawer's owner card doesn't, so an admin who
  // owns an org they open would otherwise see this button on
  // themselves. Enforcing here covers both surfaces.
  if (userProfile?.user_id && userProfile.user_id === userId) return null;
  const viewerRole = normalizeRole(userProfile?.role);
  const canReset = viewerRole === ROLES.ADMIN || viewerRole === ROLES.SUPER_ADMIN;
  if (!canReset) return null;

  const patchCaches = () => {
    // Patch every cached surface that carries this user's row so the
    // button stays hidden after the drawer is reopened. Rows are matched
    // by their own user_id field, not the query key's second element —
    // OrganizationDrawer keys its owner cache on userprofile_id ([:634])
    // while ResetMfaButton receives auth.users.id, so a key-position
    // compare would never match that surface.
    queryClient.setQueriesData(
      { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orgMembers' },
      (old) => Array.isArray(old)
        ? old.map((m) => (m?.user_id === userId ? { ...m, mfa_enabled: false } : m))
        : old,
    );
    queryClient.setQueriesData(
      { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'user' },
      (old) => (old && old.user_id === userId ? { ...old, mfa_enabled: false } : old),
    );
  };

  const handleReset = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Shared page-level step-up. Silent pass-through when the admin
      // has already verified in this Account Settings visit or has no
      // MFA factor (internal @gotrustjet bypass). Throws on cancel so
      // we bail without firing the success toast — leave the confirm
      // modal up so the admin can retry.
      await gate();
      await apiClient.resetUserMfa(userId);
      toast.success('MFA reset — the user will be prompted to enroll on next sign-in.', {
        position: 'bottom-right',
      });
      setConfirmOpen(false);
      setDidReset(true);
      patchCaches();
    } catch (err) {
      if (isMfaCancelled(err)) return; // user closed the step-up modal — silent
      toast.error(err?.message || 'Failed to reset MFA', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setConfirmOpen(false);
  };

  // Two visual variants:
  //   default → pill button used in AddMemberDrawer's Status section
  //   compact → text-link style used in OrganizationDrawer's Owner block
  const btnClass = variant === 'compact'
    ? 'inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 hover:underline'
    : 'w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className={`${btnClass} ${className}`}
      >
        <RiShieldKeyholeLine className="w-4 h-4" />
        Reset MFA
      </button>
      {confirmOpen && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-amber-700">Reset MFA?</h2>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                This will clear {memberName ? <span className="font-semibold">{memberName}</span> : 'this user'}'s
                two-factor authentication. They'll be prompted to enroll a new authenticator on their next sign-in.
              </p>
              <p className="text-xs text-gray-500">
                Only reset when the user has confirmed they lost access to their authenticator app.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                  {saving ? 'Resetting…' : 'Reset MFA'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ResetMfaButton;
